import React, { useState, useEffect } from 'react';
import useSoloStore, { BOT_CONFIGS } from '../stores/soloStore';
import { reviewAnswers } from '../Services/soloAiReviewService';
import { applySpecialRound, SPECIAL_ROUND_TITLES } from '../Services/soloSpecialRounds';
import { BotMascot, CAST, MascotAvatar, PencilMascot, scoreMood, useMyMascot } from '../Components/Mascot';

const CATEGORY_LABELS = { name: 'Name', animal: 'Animal', place: 'Place', thing: 'Thing' };
const MIN_REVIEW_MS = 1800;
const STEP_DELAY_MS = 1200;

export default function SoloReviewPhase() {
  const {
    currentLetter, categories, userAnswers, botAnswers,
    submissionTime, selectedBot, specialRounds, currentRound, submitRound,
  } = useSoloStore();

  const botConfig    = BOT_CONFIGS[selectedBot] || {};
  const myMascot     = useMyMascot();
  const specialRound = specialRounds.find((sr) => sr.round === currentRound);

  const [reviewing, setReviewing]         = useState(true);
  const [currentStep, setCurrentStep]     = useState(-1);
  const [finalUserScores, setFinalUserScores] = useState(null);
  const [finalBotScores, setFinalBotScores]   = useState(null);
  const [specialEffect, setSpecialEffect] = useState(null);
  const [visible, setVisible]             = useState(true);

  useEffect(() => {
    let cancelled = false;
    const start = Date.now();

    async function run() {
      try {
        const result = await reviewAnswers(currentLetter, categories, userAnswers, botAnswers, selectedBot);
        if (cancelled) return;

        let rawUser = Object.fromEntries(categories.map((c) => [c, result.user?.[c] ?? 0]));
        let rawBot  = Object.fromEntries(categories.map((c) => [c, result.bot?.[c]  ?? 0]));

        let effect = null;
        if (specialRound) {
          const applied = applySpecialRound(rawUser, rawBot, specialRound.type, submissionTime, userAnswers);
          rawUser = applied.userScores;
          rawBot  = applied.botScores;
          effect  = { type: specialRound.type, title: SPECIAL_ROUND_TITLES[specialRound.type], description: applied.effectDescription };
        }

        setFinalUserScores(rawUser);
        setFinalBotScores(rawBot);
        setSpecialEffect(effect);

        const elapsed = Date.now() - start;
        setTimeout(() => {
          if (cancelled) return;
          setReviewing(false);
          setCurrentStep(0);
        }, Math.max(0, MIN_REVIEW_MS - elapsed));
      } catch {
        if (!cancelled) {
          setReviewing(false);
          setFinalUserScores(Object.fromEntries(categories.map((c) => [c, 0])));
          setFinalBotScores(Object.fromEntries(categories.map((c) => [c, 0])));
          setCurrentStep(0);
        }
      }
    }

    run();
    return () => { cancelled = true; };
  }, []);

  // Auto-advance through categories then submit
  useEffect(() => {
    if (reviewing || currentStep < 0) return;

    if (currentStep >= categories.length) {
      const id = setTimeout(() => {
        submitRound(finalUserScores || {}, finalBotScores || {}, specialEffect);
      }, 500);
      return () => clearTimeout(id);
    }

    const id = setTimeout(() => {
      setVisible(false);
      setTimeout(() => { setCurrentStep((s) => s + 1); setVisible(true); }, 200);
    }, STEP_DELAY_MS);
    return () => clearTimeout(id);
  }, [reviewing, currentStep, finalUserScores, finalBotScores, specialEffect]);

  const category  = categories[currentStep];
  const uScore    = finalUserScores?.[category] ?? 0;
  const bScore    = finalBotScores?.[category]  ?? 0;
  const userValid = uScore > 0;
  const botValid  = bScore > 0;

  return (
    <div className="min-h-screen bg-orange-50 flex flex-col">
      <div className="px-5 pt-5 pb-4 text-center">
        <p className="text-2xl font-bold text-red-600">Round {currentRound} Review</p>
        <p className="text-lg text-gray-700 mt-0.5">
          Letter <span className="font-bold text-red-600">{currentLetter}</span>
        </p>
      </div>

      <div className="flex justify-center items-center gap-3 px-5 mb-6">
        {categories.map((cat, i) => {
          const isDone   = currentStep > i;
          const isActive = currentStep === i && !reviewing;
          return (
            <div key={cat} className={`rounded-full transition-all duration-300 ${
              isDone ? 'w-3 h-3 bg-emerald-500' : isActive ? 'w-4 h-4 bg-red-600' : 'w-3 h-3 bg-gray-200'
            }`} />
          );
        })}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-5">
        {reviewing ? (
          <div className="bg-white rounded-2xl shadow-md p-8 w-full max-w-sm text-center lg:max-w-md lg:p-10">
            {/* Namey and the opponent reading the answer sheet at the table */}
            <div className="relative mx-auto mb-5 w-56" aria-hidden="true">
              <div className="flex items-end justify-center gap-6">
                <PencilMascot mood="thinking" size={50} shadow={false} label="" />
                <BotMascot bot={selectedBot} state="thinking" size={62} />
              </div>
              <div className="relative -mt-3 h-4 rounded-full bg-amber-200 shadow-inner" />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-2 w-16 h-5 rounded-sm bg-white border border-gray-200 shadow-sm rotate-[-4deg]">
                <div className="mx-1.5 mt-1 h-0.5 bg-gray-200" />
                <div className="mx-1.5 mt-1 h-0.5 bg-gray-200" />
              </div>
            </div>
            <p className="text-lg font-semibold text-gray-700">Reviewing answers…</p>
            <p className="text-sm text-gray-400 mt-1">Namey and {botConfig.name || 'your opponent'} are checking both sides</p>
          </div>
        ) : currentStep < categories.length ? (
          <div className="bg-white rounded-2xl shadow-md p-6 w-full max-w-sm transition-opacity duration-200 lg:max-w-4xl lg:p-10 lg:grid lg:grid-cols-2 lg:gap-6"
               style={{ opacity: visible ? 1 : 0 }}>
            <p className="text-2xl font-bold text-red-600 text-center uppercase mb-6 lg:col-span-2 lg:text-4xl lg:mb-2">
              {CATEGORY_LABELS[category] || category}
            </p>

            <div className="border border-gray-200 rounded-xl p-4 mb-4 flex items-center gap-3 lg:mb-0 lg:p-6">
              <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-500 font-semibold mb-2">You</p>
              <p className="text-xl font-semibold text-gray-700 mb-3 lg:text-3xl">
                {userAnswers[category]?.trim() || <span className="text-gray-300 italic text-base">blank</span>}
              </p>
              {userValid ? (
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-black text-emerald-500">+{uScore}</span>
                  <span className="text-sm text-gray-500 font-semibold">points</span>
                </div>
              ) : (
                <span className="inline-block bg-red-600 text-white text-sm font-bold px-3 py-1 rounded-lg">Invalid</span>
              )}
              </div>
              <div className="flex-shrink-0" aria-hidden="true">
                <MascotAvatar
                  key={`you-${category}`}
                  mascot={myMascot}
                  mood={scoreMood(userValid, uScore)}
                  size={myMascot.base === 'pencil' ? 40 : 60}
                  shadow={false}
                  className="nm-pop"
                />
              </div>
            </div>

            <div className="border border-gray-200 rounded-xl p-4 flex items-center gap-3 lg:p-6">
              <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-500 font-semibold mb-2">{botConfig.name || 'Bot'}</p>
              <p className="text-xl font-semibold text-gray-600 mb-3 lg:text-3xl">
                {botAnswers[category]?.trim() || <span className="text-gray-300 italic text-base">blank</span>}
              </p>
              {botValid ? (
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-black text-emerald-500">+{bScore}</span>
                  <span className="text-sm text-gray-500 font-semibold">points</span>
                </div>
              ) : (
                <span className="inline-block bg-red-600 text-white text-sm font-bold px-3 py-1 rounded-lg">Invalid</span>
              )}
              </div>
              {CAST[selectedBot] && (
                <div className="flex-shrink-0" aria-hidden="true">
                  <MascotAvatar
                    key={`bot-${category}`}
                    mascot={CAST[selectedBot]}
                    mood={scoreMood(botValid, bScore)}
                    size={60}
                    shadow={false}
                    className="nm-pop"
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="w-8 h-8 border-4 border-red-200 border-t-red-600 rounded-full animate-spin" />
        )}
      </div>
    </div>
  );
}
